// Navigation from the screen registry, filtered by permission (rail sections; tab-level) [02 §8, §18; BR-PERM-003].
import {
  resolveLanding,
  screenById,
  visibleScreens,
  visibleTabs,
  type PermissionKey,
  type ScreenDef,
  type ScreenSection,
  type ScreenTab,
} from '@skm/core';

export interface NavSection {
  readonly section: ScreenSection;
  readonly screens: readonly ScreenDef[];
}

const NO_FLAGS: ReadonlySet<string> = new Set();

/**
 * Only screens the principal may view; list/dashboard/board/setup screens in the rail (records open from lists). A screen
 * that depends on a feature flag (`ScreenDef.flag`, R-NLA-3: the report assistant, `ai.nl_reports`) is listed only when
 * that flag is among the enabled `featureFlags` of `/users/me`; with none given it stays hidden (fail closed).
 */
export function buildNav(
  permissions: ReadonlySet<PermissionKey>,
  featureFlags: ReadonlySet<string> = NO_FLAGS,
): NavSection[] {
  const bySection = new Map<ScreenSection, ScreenDef[]>();
  for (const s of visibleScreens(permissions)) {
    if (s.kind === 'record' || s.kind === 'wizard') continue;
    if (s.flag !== null && !featureFlags.has(s.flag)) continue;
    const list = bySection.get(s.section) ?? [];
    list.push(s);
    bySection.set(s.section, list);
  }
  return [...bySection].map(([section, screens]) => ({ section, screens }));
}

/** Tabs the principal may see; hidden tabs are never rendered nor fetched (AC-WEB-01). */
export function tabsFor(screenId: string, permissions: ReadonlySet<PermissionKey>): ScreenTab[] {
  const s = screenById(screenId);
  return s ? visibleTabs(s, permissions) : [];
}

/** Landing route: user pick > primary role > dashboard (AC-WEB-02). */
export function landingRoute(
  permissions: ReadonlySet<PermissionKey>,
  user: string | null | undefined,
  primaryRole: string | null | undefined,
): string {
  const id = resolveLanding(permissions, { user, primaryRole });
  return screenById(id)?.route ?? '/';
}

export interface RouteMatch {
  readonly screen: ScreenDef;
  /** `:param` segments of the matched route, decoded (`/materials/fabrics/:id` → `{ id }`). */
  readonly params: Readonly<Record<string, string>>;
}

const segments = (path: string): string[] =>
  (path.split(/[?#]/)[0] ?? '').split('/').filter(Boolean);

/** A malformed escape (`%E0%A4%A`) is kept as typed rather than failing the whole route. */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

// Screens put params into API paths (`/v1/po-imports/${id}`). A decoded param holding a separator, a query/fragment
// start or a dot-segment (also percent-encoded, which the URL parser resolves) would point the request elsewhere.
const UNSAFE_PARAM = /[/\\?#\p{Cc}]/u;
const DOT_SEGMENT = /^(?:\.|%2e){1,2}$/i;
const isSafeParam = (value: string): boolean =>
  !UNSAFE_PARAM.test(value) && !DOT_SEGMENT.test(value);

/**
 * Screen whose route matches a path (static segments + `:param`), with its params. When several routes fit, the one
 * with the most static segments wins, so `/materials/fabrics/reprice` is the wizard, not record "reprice". A param
 * that is not safe to put into an API path matches no screen [CR-W2d-2].
 */
export function matchScreen(path: string, screens: readonly ScreenDef[]): RouteMatch | undefined {
  const parts = segments(path);
  let best: (RouteMatch & { readonly statics: number }) | undefined;
  for (const screen of screens) {
    const route = segments(screen.route);
    if (route.length !== parts.length) continue;
    const params: Record<string, string> = {};
    let statics = 0;
    const fits = route.every((seg, i) => {
      const part = parts[i] ?? '';
      if (seg.startsWith(':')) {
        const value = decodeSegment(part);
        params[seg.slice(1)] = value;
        return isSafeParam(value);
      }
      statics += seg === part ? 1 : 0;
      return seg === part;
    });
    if (fits && (!best || statics > best.statics)) best = { screen, params, statics };
  }
  return best && { screen: best.screen, params: best.params };
}

/** Screen whose route matches a path (see {@link matchScreen}). */
export function matchRoute(path: string, screens: readonly ScreenDef[]): ScreenDef | undefined {
  return matchScreen(path, screens)?.screen;
}

/** The list screen one segment above a record route (`/materials/fabrics/:id` → `fabrics.list`), if any. */
export function recordListScreen(
  record: ScreenDef,
  screens: readonly ScreenDef[],
): ScreenDef | undefined {
  const parts = segments(record.route);
  if (!parts.at(-1)?.startsWith(':')) return undefined;
  const parent = `/${parts.slice(0, -1).join('/')}`;
  return screens.find(
    (s) => s.route === parent && s.kind !== 'record' && s.module === record.module,
  );
}
